import { REIButton } from "../../components/ui";

const CALCULATORS = [
  {
    title: "Renovation Budget",
    description: "Plan your costs precisely and avoid overcapitalizing on your renovation projects.",
    url: "https://info.realestateinvestar.com.au/hs-fs/hub/435410/file-2523981587.xlsx",
    icon: "🏗️",
  },
  {
    title: "Subdivision Budget",
    description: "Detailed breakdown for splitting land and calculating development potential.",
    url: "https://info.realestateinvestar.com.au/hubfs/Docs_and_downloads/Real_Estate_Investar_subdivision_budget_calculator.xlsx",
    icon: "🗺️",
  },
  {
    title: "Cash Flow Budget",
    description: "Track your income, expenses, and net yields across your whole portfolio.",
    url: "https://info.realestateinvestar.com.au/hubfs/Docs_and_downloads/Real_Estate_Investar_cashflow_budget_calculator.xlsx",
    icon: "📈",
  },
  {
    title: "Development Feasibility",
    description: "Professional-grade tool to assess whether your next project is profitable.",
    url: "https://info.realestateinvestar.com.au/hubfs/Real_Estate_Investar_AU_development_feasibility_calculator.xlsx",
    icon: "🏢",
  },
];

const InvestmentCalculators = () => {
  const openCalculator = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-white ">
      <div className="mx-auto w-full">
        {/* Hero Section */}
        <section className="overflow-hidden bg-white relative">
          <div className="h-1.5 bg-gradient-to-r from-cyan-400 via-[var(--primary-color)] to-cyan-400" />

          <div className="px-4 py-4 md:px-10 md:py-6 bg-gradient-to-b from-white to-slate-50 relative z-10">
            <div className="max-w-4xl mx-auto flex flex-col items-center text-center">
              <span className="inline-flex items-center rounded-full border border-cyan-200 bg-cyan-50 text-[var(--primary-color)] text-[11px] md:text-xs font-bold uppercase tracking-wider px-3 py-1 mb-3 shadow-sm">
                Investor Toolkit
              </span>
              <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-black mb-2 mt-4 md:mb-3 md:mt-3 tracking-wide">
                Investment Calculators
              </h1>

              <p className="text-slate-600 text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
                Take the guesswork out of property investing. Download our professional-grade Excel tools for budgeting,
                cash flow, and subdivision analysis.
              </p>
            </div>
          </div>
        </section>
        {/* Featured Calculators Grid */}
        <section className="bg-gray-100 p-4 md:p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col mb-1 pb-1">
              <h2 className="text-xl md:text-[1.7rem] font-semibold text-black mb-3 md:mb-4 tracking-wide mt-2">
                Available Tools
              </h2>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full">
                <h3 className="text-black text-sm md:text-base">
                  Download the latest versions of our analysis calculators.
                </h3>

                <span className="inline-flex items-center gap-2 text-[10px] md:text-xs font-bold text-slate-400 uppercase tracking-widest bg-gray-100 px-3 py-1.5 rounded-lg self-start sm:self-auto">
                  <i className=" text-red-500" /> Excel Format (.xlsx)
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:gap-5">
              {CALCULATORS.map((calculator) => (
                <div
                  key={calculator.title}
                  className="group relative rounded-lg bg-white p-5 md:p-6 transition-all duration-300 overflow-hidden"
                >
                  {/* Subtle hover background decoration */}
                  <div className="absolute top-0 right-0 -mt-8 -mr-8 w-24 h-24 bg-cyan-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />

                  <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6 z-10">
                    <div className="flex items-start md:items-center gap-5">
                      <div className="flex items-center justify-center w-14 h-14 rounded-lg bg-slate-50 text-2xl group-hover:bg-cyan-100 transition-colors">
                        {calculator.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg md:text-xl font-bold text-black group-hover:text-black transition-colors">
                          {calculator.title}
                        </h3>
                        <p className="text-slate-600 mt-1 text-sm md:text-base leading-relaxed">
                          {calculator.description}
                        </p>
                      </div>
                    </div>

                    <div className="shrink-0 pt-2 md:pt-0">
                      <REIButton
                        id={`ic-btn-download-${calculator.title.replace(/\s+/g, "").toLowerCase()}`}
                        label="DOWNLOAD XLSX"
                        onClick={() => openCalculator(calculator.url)}
                        classNames="bg-[#238EA0] hover:bg-[#1f7a8c] border-none font-semibold px-6 py-3 w-full sm:w-auto shadow-md transition-all group-hover:bg-[#1f7a8c]"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits/Info Section */}
        <section className="border border-slate-200 shadow-xl overflow-hidden bg-white">
          <div className="px-4 md:px-10 py-10 md:py-14 text-center bg-gradient-to-br from-slate-50 via-white to-cyan-50/30 relative">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-12 bg-gradient-to-b from-slate-200 to-transparent" />
            <h2 className="text-2xl md:text-3xl font-bold text-black tracking-wide mb-8">Why Use Our Calculators?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                { title: "Save Time", desc: "Formulas pre-built by industry experts so you just enter your data." },
                { title: "Mitigate Risk", desc: "See the numbers clearly before committing thousands to a project." },
                { title: "Professional Reports", desc: "Clean layouts that you can print or present to lenders." },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="flex flex-col items-center p-6 rounded-2xl border border-slate-100 bg-white/50 shadow-sm transition-all hover:border-cyan-200 hover:bg-cyan-50/30 group"
                >
                  <div className="w-10 h-10 rounded-full bg-cyan-100 text-[var(--primary-color)] flex items-center justify-center font-bold mb-4 shadow-sm group-hover:scale-110 transition-transform">
                    {idx + 1}
                  </div>
                  <h4 className="text-xl font-semibold text-black mb-2 tracking-wide">{item.title}</h4>
                  <p className="text-slate-600 text-sm md:text-base leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default InvestmentCalculators;
